export type ConsentDecision = 'necessary' | 'all';

const STORAGE_KEY = 'rc_cookie_consent';
const CONSENT_EVENT = 'rc:consentchange';

type ConsentListener = (decision: ConsentDecision | null) => void;

function isConsentDecision(value: string | null): value is ConsentDecision {
  return value === 'necessary' || value === 'all';
}

class ConsentService {
  private listeners: ConsentListener[] = [];

  getDecision(): ConsentDecision | null {
    const stored = globalThis.localStorage?.getItem(STORAGE_KEY) ?? null;
    return isConsentDecision(stored) ? stored : null;
  }

  hasDecision(): boolean {
    return this.getDecision() !== null;
  }

  setDecision(decision: ConsentDecision): void {
    globalThis.localStorage?.setItem(STORAGE_KEY, decision);
    this.emit(decision);
  }

  clearDecision(): void {
    globalThis.localStorage?.removeItem(STORAGE_KEY);
    this.emit(null);
  }

  onChange(listener: ConsentListener): () => void {
    this.listeners.push(listener);
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) {
        return;
      }

      listener(this.getDecision());
    };

    globalThis.window?.addEventListener?.('storage', handleStorage);

    return () => {
      this.listeners = this.listeners.filter((entry) => entry !== listener);
      globalThis.window?.removeEventListener?.('storage', handleStorage);
    };
  }

  private emit(decision: ConsentDecision | null): void {
    this.listeners.forEach((listener) => listener(decision));
    globalThis.window?.dispatchEvent?.(new CustomEvent(CONSENT_EVENT, {
      detail: { decision },
    }));
  }
}

export const consentService = new ConsentService();

export function onConsentChange(listener: ConsentListener): () => void {
  return consentService.onChange(listener);
}
